import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { generateIotAutomation, getIotGenerationStatus } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';

const Step11IotGenerate = () => {
  const navigate = useNavigate();
  const { iot, project, setIot, nextStep, previousStep,markStepCompleted,goToStep  } = useOnboarding();
  
  const [generationStatus, setGenerationStatus] = useState('idle'); // idle, generating, completed, failed
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    // Auto-start generation if not already done
    if (generationStatus === 'idle' && !iot.automationJob?.suiteId) {
      handleStartGeneration();
    }
  }, []);

  const handleStartGeneration = async () => {
    setGenerationStatus('generating');
    setProgress(0);
    
    try {
      const response = await generateIotAutomation({
        project_id: project.projectId,
        test_case_ids: iot.testCaseIds,
        interaction_mode: iot.interactionMode,
        device_category: iot.device.category
      });

      if (response.success) {
        const newJobId = response.data.job_id;
        setJobId(newJobId);
        
        // Start polling for status
        pollGenerationStatus(newJobId);
      } else {
        throw new Error('Generation failed');
      }
    } catch (error) {
      console.error('Error starting generation:', error);
      setGenerationStatus('failed');
      toast.error('Failed to start automation generation');
    }
  };

  const pollGenerationStatus = async (currentJobId) => {
    const interval = setInterval(async () => {
      try {
        const response = await getIotGenerationStatus(currentJobId);
        
        if (response.success) {
          setProgress(response.data.progress);
          setCurrentStage(response.data.current_stage || response.data.message);
          
          if (response.data.status === 'completed') {
            clearInterval(interval);
            setGenerationStatus('completed');
            
            // Save suite ID to context
            setIot({
              ...iot,
              automationJob: {
                jobId: currentJobId,
                status: 'completed',
                progress: 100,
                suiteId: response.data.suite_id
              }
            });
            
            toast.success('Hardware automation plan generated successfully!');
          } else if (response.data.status === 'failed') {
            clearInterval(interval);
            setGenerationStatus('failed');
            toast.error('Generation failed');
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    }, 1500); // Poll every 1.5 seconds

    // Cleanup after 2 minutes max
    setTimeout(() => clearInterval(interval), 120000);
  };

  const handleContinue = () => {
    if (generationStatus !== 'completed') {
      toast.error('Please wait for generation to complete');
      return;
    }

    markStepCompleted(11);
    goToStep(12);
    navigate('/app/onboarding?step=12');
  };

  const handleRetry = () => {
    setGenerationStatus('idle');
    setProgress(0);
    setCurrentStage('');
    handleStartGeneration();
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={11} completedSteps={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]} />
        
        <WizardCard
          title="Generate Hardware Automation Plan"
          subtitle="Creating automated test scripts for your hardware device"
        >
          {/* Generation in progress */}
          {generationStatus === 'generating' && (
            <div>
              <div style={{
                padding: '3rem 2rem',
                textAlign: 'center'
              }}>
                {/* Progress circle */}
                <div style={{
                  width: '120px',
                  height: '120px',
                  margin: '0 auto 2rem',
                  position: 'relative'
                }}>
                  <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
                    {/* Background circle */}
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      fill="none"
                      stroke="var(--border-color)"
                      strokeWidth="8"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      fill="none"
                      stroke="var(--primary-color)"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 54}`}
                      strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress / 100)}`}
                      style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                  </svg>
                  {/* Percentage */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: 'var(--primary-color)'
                  }}>
                    {progress}%
                  </div>
                </div>

                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem'
                }}>
                  Generating Automation...
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '1rem',
                  marginBottom: '2rem'
                }}>
                  {currentStage || 'Initializing...'}
                </p>
              </div>

              {/* Generation stages */}
              <div style={{
                padding: '1.5rem',
                background: 'var(--bg-gray-50)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                  Generation Stages
                </h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {[
                    { name: 'Analyzing test cases', threshold: 0 },
                    { name: 'Mapping device commands', threshold: 30 },
                    { name: 'Generating scripts', threshold: 60 },
                    { name: 'Validating', threshold: 90 }
                  ].map((stage, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: progress > stage.threshold ? 'var(--success-color)' : 
                                   progress === stage.threshold ? 'var(--primary-color)' : 
                                   'var(--border-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        transition: 'all 0.3s ease'
                      }}>
                        {progress > stage.threshold ? '✓' : index + 1}
                      </div>
                      <span style={{
                        fontSize: '0.875rem',
                        color: progress >= stage.threshold ? 'var(--text-primary)' : 'var(--text-secondary)'
                      }}>
                        {stage.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Generation completed */}
          {generationStatus === 'completed' && (
            <div style={{
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                margin: '0 auto 2rem',
                borderRadius: '50%',
                background: 'var(--success-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem'
              }}>
                ✓
              </div>
              <h3 style={{
                fontSize: '1.75rem',
                fontWeight: '600',
                marginBottom: '0.5rem',
                color: 'var(--success-color)'
              }}>
                Automation Plan Generated!
              </h3>
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                marginBottom: '2rem'
              }}>
                Your hardware test automation suite is ready to run
              </p>

              {/* Generation summary */}
              <div style={{
                maxWidth: '500px',
                margin: '0 auto',
                padding: '2rem',
                background: 'var(--bg-gray-50)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                textAlign: 'left'
              }}>
                <h4 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  Generation Summary
                </h4>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '6px'
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Test Cases</span>
                    <span style={{ fontWeight: '600' }}>{iot.testCaseIds?.length || 0}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '6px'
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Scripts Generated</span>
                    <span style={{ fontWeight: '600' }}>5</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '6px'
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Interaction Mode</span>
                    <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                      {iot.interactionMode}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '6px'
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Suite ID</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {iot.automationJob?.suiteId?.substring(0, 12)}...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Generation failed */}
          {generationStatus === 'failed' && (
            <div style={{
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                margin: '0 auto 2rem',
                borderRadius: '50%',
                background: 'rgba(247, 37, 133, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem'
              }}>
                ✕
              </div>
              <h3 style={{
                fontSize: '1.75rem',
                fontWeight: '600',
                marginBottom: '0.5rem',
                color: 'var(--danger-color)'
              }}>
                Generation Failed
              </h3>
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                marginBottom: '2rem'
              }}>
                There was an error generating the automation plan
              </p>
              <button
                onClick={handleRetry}
                className="wizard-btn wizard-btn-primary"
              >
                🔄 Retry Generation
              </button>
            </div>
          )}

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=10');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextDisabled={generationStatus !== 'completed'}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step11IotGenerate;